import { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { ROLE } from 'api/users/constants'

import { updateAccountPassword, selectSessionStatus } from 'slices/sessionSlice'
import { INCORRECT_OLD_PASSWORD_MESSAGE, ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'

import { InputFormat, CustomModal } from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'
import { COLUMN_SIZES } from 'components/common/constants'

type Props = {
  isOpen: boolean
  onSuccess: () => void
  onCancel: () => void
}

const PasswordChange = ({ isOpen, onSuccess, onCancel }: Props) => {
  const { user, isRequesting, errorMessage } = useSelector(selectSessionStatus, shallowEqual)
  const [modalErrorMessage, setModalErrorMessage] = useState<string | undefined>(undefined)
  const [oldPassword, setOldPassword] = useState<string | undefined>(undefined)
  const [newPassword, setNewPassword] = useState<string | undefined>(undefined)
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined)
  const [oldPasswordValidity, setOldPasswordValidity] = useState(false)
  const [newPasswordValidity, setNewPasswordValidity] = useState(false)
  const [confirmationValidity, setConfirmationValidity] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const dispatch = useDispatch()

  const disabled = useMemo(
    () =>
      !(
        oldPassword &&
        oldPasswordValidity &&
        newPassword &&
        newPasswordValidity &&
        confirmation &&
        confirmationValidity
      ),
    [oldPassword, oldPasswordValidity, newPassword, newPasswordValidity, confirmation, confirmationValidity]
  )

  const initInputState = () => {
    setOldPassword(undefined)
    setNewPassword(undefined)
    setConfirmation(undefined)
    setModalErrorMessage(undefined)
    setSubmitted(false)
  }

  const handleSaveClick = () => {
    if (oldPassword && newPassword) {
      setSubmitted(true)
      dispatch(updateAccountPassword({ oldPassword, newPassword }))
    }
  }
  const handleCancelClick = () => {
    initInputState()
    onCancel()
  }

  useEffect(() => {
    if (isRequesting || !submitted) {
      return
    }
    if (errorMessage === '') {
      initInputState()
      onSuccess()
    } else {
      if (errorMessage === INCORRECT_OLD_PASSWORD_MESSAGE) {
        setModalErrorMessage('現在のパスワードが間違っています')
      } else if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        // ENABLE_DIALOGのときにはエラーダイアログが出るのでNotificationは出さない
        setModalErrorMessage('保存できませんでした')
      }
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, onSuccess, dispatch])

  return (
    <CustomModal
      isOpen={isOpen}
      title="パスワードを変更"
      approveDisabled={disabled}
      errorMessage={modalErrorMessage}
      onCancel={handleCancelClick}
      onApprove={handleSaveClick}
      onHideNotification={() => setModalErrorMessage(undefined)}
    >
      <div className="pb-2">
        <div>
          <InputFormat
            label="現在のパスワード"
            type="password"
            placeholder="現在のパスワード"
            value={oldPassword}
            size={COLUMN_SIZES.MIDDLE}
            maxLength={32}
            onChange={value => setOldPassword(value)}
            validations={[Rules.MinLength(8), Rules.Password]}
            onValidate={setOldPasswordValidity}
          />
        </div>

        <div className="mt-4">
          {user.userHasTenants[0].role !== ROLE.TENANT_ADMIN && (
            <div className="mb-2">
              新しいパスワード（半角英数字8文字以上・大文字・小文字・数字を含む）を入力してください。
            </div>
          )}

          <InputFormat
            label="新しいパスワード"
            type="password"
            placeholder="新しいパスワード"
            value={newPassword}
            size={COLUMN_SIZES.MIDDLE}
            maxLength={32}
            onChange={value => setNewPassword(value)}
            validations={[Rules.MinLength(8), Rules.Password, Rules.DifferentPassword(oldPassword)]}
            onValidate={setNewPasswordValidity}
          />
        </div>

        <div className="mt-4">
          <InputFormat
            label="パスワードの確認"
            type="password"
            placeholder="パスワードの確認"
            value={confirmation}
            size={COLUMN_SIZES.MIDDLE}
            maxLength={32}
            onChange={value => setConfirmation(value)}
            validations={[Rules.MinLength(8), Rules.Password, Rules.PasswordConfirmation(newPassword)]}
            onValidate={setConfirmationValidity}
          />
        </div>
      </div>
    </CustomModal>
  )
}

export default PasswordChange
